import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStopwatch } from 'react-timer-hook';
import { Form } from 'antd';
import moment from 'moment';
import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import {
  convertDaysHrsMinsToMins,
  convertHrsMinsToMins,
  convertMinsToHrsMins,
  getDateDifferenceInMinutes,
  getOffsetTimestamp,
} from 'utils/string.utils';
import useWebSocket from 'utils/useWebSocket';
import timeTracking, { TIME_ENTRY_WS_EVENT, isLocalEcho, TimeEntryWsEvent } from 'services/timeTracking';
import { RootState } from 'store';
import {
  addActiveLogData,
  getActiveLogsSilent,
  removeActiveLogData,
  updateActiveLogData,
} from 'store/container/activeLogs/activeLogs-slice';
import { setGlobalLogTrigger } from '../../store/container/globalLog/globalLog-slice';

interface UseTimerOptions {
  task_id: string | undefined;
  globalActiveTimer: boolean;
  timerContent: any;
  onAddEntry: any;
  onClose: () => void;
}

const useTimer = ({ task_id, globalActiveTimer, timerContent, onAddEntry, onClose }: UseTimerOptions) => {
  const user = useSelector((store: any) => store.auth.user);
  const { loading, data: dataLogs } = useSelector((store: RootState) => store.activeLogs);

  const { minutes, hours, days, seconds, isRunning, start, pause, reset } = useStopwatch({ autoStart: false });
  const [saving, setSaving] = useState(false);
  const [loadingTotal, setLoadingTotal] = useState(false);
  const [totalTime, setTotalTime] = useState({
    userTotal: 0,
    taskTotal: 0,
  });

  const current = useMemo(() => {
    if (globalActiveTimer) {
      return dataLogs?.find((x) => x.status === 'running') ?? null;
    }

    return timerContent ?? dataLogs?.find((x) => x.source === 'task' && x.source_uuid === task_id);
  }, [dataLogs, task_id, timerContent, globalActiveTimer]);

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Tracks whether the user manually edited the time field. That field is continuously
  // rewritten with the live stopwatch value, so its value alone cannot signal an edit.
  const timeEditedRef = useRef(false);
  useEffect(() => {
    timeEditedRef.current = false;
  }, [current?.uuid, current?.status]);

  // While the time field has focus the live stopwatch must not rewrite it: reformatting a
  // partially typed value (e.g. "00:60" -> "01:00") shifts the digits under the cursor and
  // breaks the next keystroke. TimerForm re-syncs and normalises the field on blur.
  const timeFocusedRef = useRef(false);
  const onTimeFocusChange = (focused: boolean) => {
    timeFocusedRef.current = focused;
  };

  // Restores the stopwatch and form to the values held by `current` (the persisted timer),
  // discarding any local edits. Used both on load and to revert a cancelled edit.
  const syncFromCurrent = useCallback(() => {
    if (current) {
      const offsetTime = new Date();

      const totalSeconds =
        (current.minutes + (!current.started_at ? 0 : getDateDifferenceInMinutes(current.started_at))) * 60;
      offsetTime.setSeconds(offsetTime.getSeconds() + Math.round(totalSeconds));

      reset(offsetTime, current.status === 'running');

      form.setFieldsValue({
        date: moment(current.date),
        description: current.comment ?? '',
        user_id: current.user_uuid,
      });
    } else {
      reset(undefined, false);
      form.resetFields();
    }
  }, [current, form, reset]);

  // Whether the user changed anything in the form relative to the persisted timer.
  const hasFormChanges = () => {
    const time = convertHrsMinsToMins(form.getFieldValue('time'));
    const description = form.getFieldValue('description') ?? '';
    const date = moment(form.getFieldValue('date')).format('YYYY-MM-DD');

    if (!current?.uuid) {
      return time !== 0 || description !== '';
    }

    const dateChanged = moment(current.date).format('YYYY-MM-DD') !== date;
    const descriptionChanged = (current.comment ?? '') !== description;

    return timeEditedRef.current || dateChanged || descriptionChanged;
  };

  const updateTimer = async () => {
    // Auto-save on close is disabled for now — time entries are only persisted through
    // the "Add entry" action. Flip this guard to re-enable the update-on-close behaviour.
    const autoSaveOnClose = false;
    if (!autoSaveOnClose) {
      // Closing/cancelling must never persist edits, to Redux or the backend. If the
      // user changed anything, discard it and restore the original (persisted) state.
      if (hasFormChanges()) {
        syncFromCurrent();
        timeEditedRef.current = false;
      }
      return;
    }

    const time = convertHrsMinsToMins(form.getFieldValue('time'));
    const date = moment(form.getFieldValue('date')).format('YYYY-MM-DD');
    const description = form.getFieldValue('description');

    if ((!current?.uuid && time === 0) || current?.minutes === time) {
      return;
    }

    setSaving(true);

    try {
      if (current?.uuid) {
        await timeTracking.updateEntry(current.uuid, {
          date,
          minutes: time,
          description: description ?? '',
        });

        if (isMountedRef.current) {
          dispatch(updateActiveLogData({ ...current, date, minutes: time, comment: description ?? '' }));
        }
      } else if (task_id) {
        const timer = await timeTracking.createManualPausedEntry({
          source: 'task',
          sourceUuid: task_id,
          userUuid: user.uuid,
          date,
          minutes: time,
          description: description ?? '',
        });

        if (isMountedRef.current) {
          dispatch(addActiveLogData(timer));
        }
      }

      if (onAddEntry && isMountedRef.current) {
        onAddEntry();
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const startTimer = async () => {
    if (!dataLogs) return;

    const fallbackStatus = isRunning;
    const fallbackTimer = convertDaysHrsMinsToMins(days, hours, minutes);

    setSaving(true);
    start();

    try {
      const timer =
        current?.uuid && current.status === 'paused'
          ? await timeTracking.resumeTimer(current.uuid)
          : await timeTracking.startTimer({ source: 'task', sourceUuid: task_id ?? current?.source_uuid ?? '' });

      if (isMountedRef.current) {
        const exists = dataLogs.some((x) => x.uuid === timer.uuid);
        dispatch(exists ? updateActiveLogData(timer) : addActiveLogData(timer));

        dispatch(getActiveLogsSilent());
      }
    } catch (error) {
      reset(getOffsetTimestamp(fallbackTimer), fallbackStatus);
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const pauseTimer = async () => {
    const fallbackStatus = isRunning;
    const fallbackTimer = convertDaysHrsMinsToMins(days, hours, minutes);

    if (!current?.uuid) return;

    setSaving(true);
    pause();

    try {
      const timer = await timeTracking.pauseTimer(current.uuid);

      if (isMountedRef.current) {
        dispatch(updateActiveLogData(timer));
      }
    } catch (error) {
      reset(getOffsetTimestamp(fallbackTimer), fallbackStatus);
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const resetTimer = async () => {
    setSaving(true);

    try {
      if (current?.uuid) {
        await timeTracking.deleteEntry(current.uuid);
      }

      if (isMountedRef.current) {
        dispatch(removeActiveLogData(current?.uuid));

        reset(undefined, false);

        form.resetFields();

        onClose();
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const addEntry = async () => {
    setSaving(true);

    try {
      const formData = await form.validateFields();
      const time = convertHrsMinsToMins(form.getFieldValue('time'));
      const stopwatchMinutes = convertDaysHrsMinsToMins(days, hours, minutes);
      const finalMinutes = stopwatchMinutes > time ? stopwatchMinutes : time;
      const date = moment(formData.date).format('YYYY-MM-DD');

      if (current?.uuid) {
        await timeTracking.completeTimer(current.uuid, {
          minutes: finalMinutes,
          description: formData.description,
          date,
        });

        dispatch(removeActiveLogData(current.uuid));
      } else if (task_id) {
        await timeTracking.createEntry({
          source: 'task',
          sourceUuid: task_id,
          userUuid: formData.user_id ?? user.uuid,
          date,
          minutes: finalMinutes,
          description: formData.description,
        });
      }

      dispatch(setGlobalLogTrigger());

      const totalSourceUuid = current?.source === 'task' ? current.source_uuid : task_id;
      const totalResponse = await apiRequests.get(
        `${apiRoutes.PROJECT_TASKS}/${totalSourceUuid}/time-logs/${user.uuid}`,
      );

      if (isMountedRef.current) {
        setTotalTime({
          userTotal: parseInt(totalResponse.data.data.sum_user_completed, 10),
          taskTotal: parseInt(totalResponse.data.data.sum_completed, 10),
        });

        form.setFieldsValue({
          description: '',
          time: '00:00',
          date: moment(new Date(Date.now())),
        });

        reset(undefined, false);

        onClose();

        if (onAddEntry) {
          onAddEntry();
        }
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const fetchTotalTimeLog = async (abort?: AbortController) => {
    setLoadingTotal(true);

    try {
      const totalResponse = await apiRequests.get(`${apiRoutes.PROJECT_TASKS}/${task_id}/time-logs/${user.uuid}`, {}, {}, abort);

      if (isMountedRef.current) {
        setTotalTime({
          userTotal: parseInt(totalResponse.data.data.sum_user_completed, 10),
          taskTotal: parseInt(totalResponse.data.data.sum_completed, 10),
        });
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      asyncErrorHandler(error);
    } finally {
      if (isMountedRef.current) {
        setLoadingTotal(false);
      }
    }
  };

  const onChangeTimer = (value: string) => {
    if (!current) return;

    timeEditedRef.current = true;

    const offsetTime = new Date();

    offsetTime.setMinutes(offsetTime.getMinutes() + convertHrsMinsToMins(value));

    reset(offsetTime, current.status === 'running');
  };

  useEffect(() => {
    const abort = new AbortController();
    const buttonClose = document.querySelector('.ant-modal-close');

    const handleClose = () => {
      onClose();
    };

    if (task_id && buttonClose) {
      buttonClose.addEventListener('click', handleClose);
    }

    if (task_id) {
      fetchTotalTimeLog(abort);
    }

    return () => {
      abort.abort();

      if (task_id && buttonClose) {
        buttonClose.removeEventListener('click', handleClose);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task_id]);

  useEffect(() => {
    if (current && !timeFocusedRef.current) {
      form.setFieldsValue({ time: convertMinsToHrsMins(convertDaysHrsMinsToMins(days, hours, minutes)) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes, hours, days]);

  useEffect(() => {
    syncFromCurrent();
  }, [syncFromCurrent]);

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: TIME_ENTRY_WS_EVENT,
      callback: (event: TimeEntryWsEvent) => {
        if (isLocalEcho(event)) return;

        if (task_id && event.payload.source === 'task' && event.payload.source_uuid === task_id) {
          fetchTotalTimeLog();
        }
      },
    },
  });

  return {
    minutes,
    hours,
    days,
    seconds,
    isRunning,
    form,
    totalTime,
    saving,
    loading,
    loadingTotal,
    current,
    start,
    pause,
    startTimer,
    pauseTimer,
    addEntry,
    updateTimer,
    resetTimer,
    onChangeTimer,
    onTimeFocusChange,
  };
};

export default useTimer;
